import {
  PALETTE,
  getMessageTypeFromPalette,
  hasFixedMessageTypePalette,
  isMapTransitionPalette,
  usesTransitions
} from "../workflowPaletteConstants";
import { getPaletteCatalogEntries, getDefaultValue } from "../workflowSchemaUtils";

/** Personalization chips shown on message fields — inserted as {{name}}. */
export const MESSAGE_VARIABLES = [{ token: "{{user.name}}", label: "User name" }];

const PALETTE_FALLBACK_LABELS = {
  [PALETTE.COLLECT_INPUT]: "Collect customer input",
  [PALETTE.CONDITION]: "If / else branch",
  [PALETTE.ACTION]: "Automated action",
  [PALETTE.HANDOFF]: "Hand off to agent",
  [PALETTE.END]: "End conversation",
  [PALETTE.INVOKE_WORKFLOW]: "Run sub-flow",
  [PALETTE.TRANSFER_WORKFLOW]: "Switch to another flow"
};

export const FRIENDLY_FIELD_LABELS = {
  recipientId: "Recipient ID",
  storeAs: "Save answer as",
  prompt: "Message to send",
  invalidTypeMessage: "If wrong type, reply with",
  expectedType: "Customer should send",
  text: "Message text",
  message: "Message",
  options: "Reply buttons",
  items: "Menu options",
  label: "Button text",
  buttonLabel: "Menu button text",
  sectionTitle: "Menu section title",
  caption: "Caption",
  url: "Media file",
  mediaId: "Uploaded file ID",
  filename: "File name",
  templateId: "Template name",
  language: "Language",
  bodyParams: "Template placeholders",
  headerImageLink: "Header image link",
  headerImageMediaId: "Header image media ID",
  action: "Action to run",
  input: "Action details",
  workflowId: "Switch to which flow?",
  workflowVersion: "Flow version",
  messageToUser: "Message to customer",
  priority: "Priority",
  queueId: "Support team",
  logic: "Match rules",
  conditions: "Rules",
  params: "Query Parameters",
  _note: "Note",
  _dynamic: "Variables"
};

/** Fields handled automatically — never shown in the inspector. */
const HIDDEN_FIELD_NAMES = new Set([
  "saveToContext",
  "retryPolicy",
  "contextMapping",
  "resultMapping",
  "workflowVersion",
  "invalidTypeMessage",
  "mediaId",
  "headerImageMediaId"
]);

/** Nested array item fields hidden from non-coders (values auto-generated). */
const HIDDEN_NESTED_FIELD_NAMES = new Set(["id"]);

export function getPaletteDisplayName(paletteType, catalogData) {
  if (!paletteType) return "";
  const entry = getPaletteCatalogEntries(catalogData).find((e) => e.paletteType === paletteType);
  return entry?.label || PALETTE_FALLBACK_LABELS[paletteType] || paletteType;
}

export function getFriendlyFieldLabel(field, paletteType) {
  if (field.name === "id" && isMapTransitionPalette(paletteType) && /\.buttons(\.|$)/.test(paletteType)) {
    return "Button reference";
  }
  if (field.name === "workflowId" && paletteType === PALETTE.INVOKE_WORKFLOW) {
    return "Sub-flow to run";
  }
  if (field.name === "url" && paletteType === PALETTE.ACTION) {
    return "URL";
  }
  return FRIENDLY_FIELD_LABELS[field.name] || field.label || field.name;
}

export function shouldHideInspectorField(fieldName, paletteType) {
  if (paletteType === PALETTE.COLLECT_INPUT && (fieldName === "storeAs" || fieldName === "invalidTypeMessage")) {
    return false;
  }
  if (HIDDEN_FIELD_NAMES.has(fieldName)) return true;
  if (fieldName === "type" && (hasFixedMessageTypePalette(paletteType) || paletteType === "whatsapp.notify" || paletteType === "whatsapp.await")) return true;
  if (fieldName === "id" && isMapTransitionPalette(paletteType) && /\.buttons(\.|$)/.test(paletteType)) {
    return true;
  }
  return false;
}

export function shouldHideNestedField(fieldName, parentFieldName, paletteType) {
  if (HIDDEN_NESTED_FIELD_NAMES.has(fieldName)) return true;
  if (HIDDEN_FIELD_NAMES.has(fieldName)) return true;
  if (fieldName === "id" && parentFieldName === "options") return true;
  if (fieldName === "type" && parentFieldName === "message" && (hasFixedMessageTypePalette(paletteType) || paletteType === "whatsapp.notify" || paletteType === "whatsapp.await")) {
    return true;
  }
  return false;
}

export function resolveConfigMessageType(config = {}, paletteType) {
  const fromPalette = getMessageTypeFromPalette(paletteType);
  if (fromPalette) return fromPalette;
  return config.message?.type || config.type || "text";
}

export function shouldShowSaveToContextUi(paletteType, workflowType, config) {
  if (usesTransitions(workflowType)) return true;
  if (workflowType === "ACTION" || paletteType === PALETTE.ACTION) {
    if (config?.action === "SET_VARIABLE" || config?.action === "CLEAR_VARIABLE") {
      return false;
    }
    return !!config?.action;
  }
  return (
    workflowType === "INVOKE_WORKFLOW" ||
    paletteType === PALETTE.INVOKE_WORKFLOW
  );
}

export function getAutoSaveToContext(paletteType) {
  if (isMapTransitionPalette(paletteType)) {
    return { selectedOption: "$event.message.payload.id" };
  }
  return null;
}

export function deriveStoreAsFromLabel(label = "") {
  const words = label
    .replace(/collect\s+/i, "")
    .replace(/[^a-zA-Z0-9\s]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) return "userInput";

  const [first, ...rest] = words;
  const camel =
    first.toLowerCase() +
    rest.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join("");

  return camel.replace(/[^a-zA-Z0-9]/g, "") || "userInput";
}

export function slugifyButtonId(label = "") {
  const slug = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return slug || "option";
}

function ensureUniqueSiblingIds(rows = [], slugify = (label) => label) {
  const usedIds = new Set();
  return rows.map((row, index) => {
    const label = row?.label ?? row?.title ?? "";
    let id = row?.id || slugify(label) || `option_${index + 1}`;
    if (usedIds.has(id)) {
      const baseId = slugify(label) || id || `option_${index + 1}`;
      let suffix = 2;
      let candidate = `${baseId}_${suffix}`;
      while (usedIds.has(candidate)) {
        suffix += 1;
        candidate = `${baseId}_${suffix}`;
      }
      id = candidate;
    }
    usedIds.add(id);
    return { ...row, id };
  });
}

/** Keep button transition ids unique and aligned with labels. */
export function normalizeButtonOptions(options = []) {
  if (!Array.isArray(options)) return [];
  return ensureUniqueSiblingIds(options, slugifyButtonId).map((option) => ({
    ...option,
    label: option?.label ?? "",
  }));
}

export function normalizeListItems(items = []) {
  if (!Array.isArray(items)) return [];
  return ensureUniqueSiblingIds(items, slugifyButtonId).map((item) => ({
    ...item,
    label: item?.label ?? "",
    title: item?.title ?? item?.label ?? "",
  }));
}

/** Catalog list.await uses expression items; defaultConfig may ship []. */
export function coerceListItemsForPalette(items, paletteType) {
  if (!/\.list(\.|$)/.test(paletteType || "")) return items;
  if (Array.isArray(items)) {
    return items.length ? normalizeListItems(items) : "";
  }
  return items ?? "";
}

/** Value shown in inspector controls — expression fields must not receive []. */
export function resolveInspectorFieldValue(field, value) {
  if (field?.type === "expression") {
    if (Array.isArray(value) || value == null) return "";
    return String(value);
  }
  if (field?.type === "array") {
    return Array.isArray(value) ? value : [];
  }
  if (value !== undefined && value !== null) return value;
  return getDefaultValue(field);
}

export function getSaveToContextValuePlaceholder(paletteType, workflowType) {
  if (/\.buttons(\.|$)/.test(paletteType || "") || /\.list(\.|$)/.test(paletteType || "")) {
    return "$event.message.payload.id";
  }
  if (workflowType === "AWAIT_USER_REPLY") {
    return "$event.message.text";
  }
  if (workflowType === "ACTION" || paletteType === PALETTE.ACTION) {
    return "e.g. xyz";
  }
  return "From action result";
}

const COLLECT_INPUT_TYPE_DEFAULTS = {
  text: {
    prompt: "Enter your name",
    invalidTypeMessage: "Please send text only"
  },
  image: {
    prompt: "Upload an image",
    invalidTypeMessage: "Please upload an image"
  },
  document: {
    prompt: "Upload a document",
    invalidTypeMessage: "Please upload a document"
  },
  video: {
    prompt: "Upload a video",
    invalidTypeMessage: "Please upload a video"
  },
  button: {
    prompt: "Please tap a button to continue",
    invalidTypeMessage: "Please tap one of the buttons"
  },
  list: {
    prompt: "Please choose an option from the list",
    invalidTypeMessage: "Please select an option from the list"
  }
};

export function getCollectInputTypeDefaults(expectedType = "text") {
  return COLLECT_INPUT_TYPE_DEFAULTS[expectedType] || COLLECT_INPUT_TYPE_DEFAULTS.text;
}

/** When expectedType changes, refresh prompt / invalidTypeMessage if still on prior defaults. */
export function applyCollectInputExpectedTypeChange(config = {}, prevExpectedType, nextExpectedType) {
  const prevDefaults = getCollectInputTypeDefaults(prevExpectedType);
  const nextDefaults = getCollectInputTypeDefaults(nextExpectedType);
  const next = { ...config, expectedType: nextExpectedType };

  if (config.prompt == null || config.prompt === prevDefaults.prompt) {
    next.prompt = nextDefaults.prompt;
  }
  if (config.invalidTypeMessage == null || config.invalidTypeMessage === prevDefaults.invalidTypeMessage) {
    next.invalidTypeMessage = nextDefaults.invalidTypeMessage;
  }
  return next;
}

export function normalizeCollectInputConfig(config = {}, nodeLabel = "") {
  const next = { ...config };
  const expectedType = next.expectedType || "text";
  next.expectedType = expectedType;

  if (next.storeAs == null) {
    next.storeAs = deriveStoreAsFromLabel(nodeLabel);
  }

  const typeDefaults = getCollectInputTypeDefaults(expectedType);
  if (next.prompt == null) {
    next.prompt = typeDefaults.prompt;
  }
  if (next.invalidTypeMessage == null) {
    next.invalidTypeMessage = typeDefaults.invalidTypeMessage;
  }
  return next;
}

export function fieldSupportsVariables(field) {
  return Boolean(field.supportsTemplates);
}

export function resolveWorkflowVersion(workflow) {
  return workflow?.activeVersion || workflow?.draftVersion || workflow?.version || 1;
}

export function getWorkflowNodeDescription(paletteType, workflowName) {
  if (!workflowName) return "";
  if (paletteType === PALETTE.INVOKE_WORKFLOW) return `Run: ${workflowName}`;
  if (paletteType === PALETTE.TRANSFER_WORKFLOW) return `Switch to: ${workflowName}`;
  return workflowName;
}

export function getWorkflowTransferDescription(workflowName) {
  return getWorkflowNodeDescription(PALETTE.TRANSFER_WORKFLOW, workflowName);
}

export function usesWorkflowPicker(paletteType) {
  return paletteType === PALETTE.TRANSFER_WORKFLOW || paletteType === PALETTE.INVOKE_WORKFLOW;
}

const MEDIA_UPLOAD_FIELD_NAMES = new Set(["url", "headerImageLink"]);

export function isMediaUploadField(fieldName, config = {}, parentFieldName, paletteType) {
  if (MEDIA_UPLOAD_FIELD_NAMES.has(fieldName)) {
    if (fieldName === "headerImageLink") return true;
    if (fieldName === "url" && parentFieldName === "message") {
      const messageType = resolveConfigMessageType(config, paletteType);
      return messageType === "image" || messageType === "video" || messageType === "document";
    }
  }
  return false;
}

/** Infer image / video / document for CRM upload validation. */
export function resolveWorkflowMediaType(fieldName, config = {}, parentFieldName, paletteType) {
  if (fieldName === "headerImageLink") return "image";

  if (fieldName === "url" || parentFieldName === "message") {
    const messageType = resolveConfigMessageType(config, paletteType);
    if (messageType === "video" || messageType === "document") return messageType;
    return "image";
  }

  if (config.expectedType === "image" || config.expectedType === "video" || config.expectedType === "document") {
    return config.expectedType;
  }

  return "image";
}

export function applyMediaUploadMeta(nextObject, fieldName, meta) {
  if (!meta) return nextObject;

  let next = { ...nextObject };
  if (meta.filename !== undefined) {
    next.filename = meta.filename;
  }
  if (meta.pendingFile !== undefined) {
    next.pendingFile = meta.pendingFile;
  }
  if (meta.clearPendingFile) {
    delete next.pendingFile;
  }
  if (meta.clearMediaId || fieldName === "url" || fieldName === "headerImageLink") {
    delete next.mediaId;
    delete next.headerImageMediaId;
  }
  return next;
}
